import { Checkbox, Divider, Group, Select, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { Paths, PathValue } from "@mctypes/global.types";
import * as R from "ramda";
import React, { useEffect } from "react";
import { EierBrukerAnnetEnum } from "@mcapps/shared/reportEnums";
import {
  IElektroSamsvarserklaringEkomnettRapport,
  EKOMNETT_INSTALLATION_TYPES,
} from "@mcapps/shared/reportSchemas/elektroSamsvarserklaringEkomnettSchema";
import { dividerWeight, labelWeight } from "../../reportBoardConstants";

const RapportSamsvarserklaringEkomnett: React.FC<{
  data: IElektroSamsvarserklaringEkomnettRapport;
  createdAt?: string;
  onUpdateEditData: (data: IElektroSamsvarserklaringEkomnettRapport) => void;
  projectId?: string;
}> = ({ data, createdAt, onUpdateEditData, projectId }) => {
  const [editData, setEditData] = React.useState<IElektroSamsvarserklaringEkomnettRapport>(R.clone(data));

  useEffect(() => {
    setEditData(R.clone(data) ?? ({} as IElektroSamsvarserklaringEkomnettRapport));
  }, [createdAt, data]);

  useEffect(() => {
    onUpdateEditData(editData);
  }, [editData]);

  const updateData = <P extends Paths<IElektroSamsvarserklaringEkomnettRapport, 6>>(
    path: P,
    value: PathValue<IElektroSamsvarserklaringEkomnettRapport, P>,
  ) => {
    setEditData((prev) => R.assocPath(path.split("."), value, prev));
    onUpdateEditData(R.assocPath(path.split("."), value, editData));
  };

  const standards = editData.anleggsbeskrivelse?.utfoertIht ?? [];
  const nek700 = standards.find((standard) => standard.lovverk === "NEK 700");
  const otherStandards = standards.filter((standard) => standard.lovverk !== "NEK 700");
  const installationTypes = editData.anleggsbeskrivelse?.typeAnlegg?.split(", ").filter(Boolean) ?? [];
  const legacyInstallationTypes = installationTypes.filter(
    (type) => !EKOMNETT_INSTALLATION_TYPES.some((option) => option === type),
  );

  return (
    <Stack p="md" style={{ maxWidth: 700, margin: "0 auto" }}>
      {editData && (
        <>
          <Text size="xl" align="center" fw={500}>
            Samsvarserklæring EKOMNETT:
          </Text>

          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Generell info
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Group grow>
              <Text fw={labelWeight}>Utarbeidet av:</Text>
              <TextInput
                size="sm"
                value={editData.generellInfo?.utarbeidetAv ?? ""}
                onChange={(e) => updateData("generellInfo.utarbeidetAv", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Kontrollert av:</Text>
              <TextInput
                size="sm"
                value={editData.generellInfo?.kontrollertAv ?? ""}
                onChange={(e) => updateData("generellInfo.kontrollertAv", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Godkjent av:</Text>
              <TextInput
                size="sm"
                value={editData.generellInfo?.godkjentAv ?? ""}
                onChange={(e) => updateData("generellInfo.godkjentAv", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Dokumentnummer:</Text>
              <TextInput
                size="sm"
                value={editData.generellInfo?.dokumentNr ?? ""}
                onChange={(e) => updateData("generellInfo.dokumentNr", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Revisjonsnummer:</Text>
              <TextInput
                size="sm"
                value={editData.generellInfo?.revisjonsNr ?? ""}
                onChange={(e) => updateData("generellInfo.revisjonsNr", e.target.value)}
              />
            </Group>
          </Stack>

          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Oppdragsgiver
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Group grow>
              <Text fw={labelWeight}>Navn:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsgiver?.navn ?? ""}
                onChange={(e) => updateData("oppdragsgiver.navn", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Oppdragsnummer:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsgiver?.oppdragsnummer ?? ""}
                onChange={(e) => updateData("oppdragsgiver.oppdragsnummer", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Kontaktperson:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsgiver?.kontaktperson ?? ""}
                onChange={(e) => updateData("oppdragsgiver.kontaktperson", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Adresse:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsgiver?.adresse ?? ""}
                onChange={(e) => updateData("oppdragsgiver.adresse", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Postboks:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsgiver?.postboks ?? ""}
                onChange={(e) => updateData("oppdragsgiver.postboks", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Postnummer:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsgiver?.postNr ?? ""}
                onChange={(e) => updateData("oppdragsgiver.postNr", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Sted:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsgiver?.sted ?? ""}
                onChange={(e) => updateData("oppdragsgiver.sted", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Telefon:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsgiver?.telefon ?? ""}
                onChange={(e) => updateData("oppdragsgiver.telefon", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>E-post:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsgiver?.email ?? ""}
                onChange={(e) => updateData("oppdragsgiver.email", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Kundenummer:</Text>
              <TextInput
                size="sm"
                value={editData.oppdragsgiver?.kundenummer ?? ""}
                onChange={(e) => updateData("oppdragsgiver.kundenummer", e.target.value)}
              />
            </Group>
          </Stack>

          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Anleggsadresse
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Group grow>
              <Text fw={labelWeight}>Navn:</Text>
              <TextInput
                size="sm"
                value={editData.anleggsadresse?.navn ?? ""}
                onChange={(e) => updateData("anleggsadresse.navn", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Adresse:</Text>
              <TextInput
                size="sm"
                value={editData.anleggsadresse?.adresse ?? ""}
                onChange={(e) => updateData("anleggsadresse.adresse", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Postnummer:</Text>
              <TextInput
                size="sm"
                value={editData.anleggsadresse?.postNr ?? ""}
                onChange={(e) => updateData("anleggsadresse.postNr", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Sted:</Text>
              <TextInput
                size="sm"
                value={editData.anleggsadresse?.sted ?? ""}
                onChange={(e) => updateData("anleggsadresse.sted", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Husnummer:</Text>
              <TextInput
                size="sm"
                value={editData.anleggsadresse?.husNr ?? ""}
                onChange={(e) => updateData("anleggsadresse.husNr", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Husnummer bokstav:</Text>
              <TextInput
                size="sm"
                value={editData.anleggsadresse?.husNrBokstav ?? ""}
                onChange={(e) => updateData("anleggsadresse.husNrBokstav", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Boligmappe-ID:</Text>
              <TextInput
                size="sm"
                value={editData.anleggsadresse?.boligMappeId ?? ""}
                onChange={(e) => updateData("anleggsadresse.boligMappeId", e.target.value)}
              />
            </Group>
          </Stack>

          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Kontaktperson
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Group grow>
              <Text fw={labelWeight}>Eier/Bruker/Annet:</Text>
              <Select
                size="sm"
                data={["Eier", "Bruker", "Annet"]}
                value={editData.kontaktperson?.eierBrukerAnnet ?? ""}
                onChange={(val) => updateData("kontaktperson.eierBrukerAnnet", val as EierBrukerAnnetEnum)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Navn:</Text>
              <TextInput
                size="sm"
                value={editData.kontaktperson?.navn ?? ""}
                onChange={(e) => updateData("kontaktperson.navn", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Telefon:</Text>
              <TextInput
                size="sm"
                value={editData.kontaktperson?.telefon ?? ""}
                onChange={(e) => updateData("kontaktperson.telefon", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>E-post:</Text>
              <TextInput
                size="sm"
                value={editData.kontaktperson?.email ?? ""}
                onChange={(e) => updateData("kontaktperson.email", e.target.value)}
              />
            </Group>
          </Stack>

          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Ordreopplysninger
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Group grow>
              <Text fw={labelWeight}>Ordrenummer:</Text>
              <TextInput
                size="sm"
                value={editData.ordreopplysninger?.ordreNummer ?? ""}
                onChange={(e) => updateData("ordreopplysninger.ordreNummer", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Startdato:</Text>
              <TextInput
                size="sm"
                type="date"
                value={editData.ordreopplysninger?.startDato ?? ""}
                onChange={(e) => updateData("ordreopplysninger.startDato", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Avsluttet dato:</Text>
              <TextInput
                size="sm"
                type="date"
                value={editData.ordreopplysninger?.avsluttetDato ?? ""}
                onChange={(e) => updateData("ordreopplysninger.avsluttetDato", e.target.value)}
              />
            </Group>
          </Stack>

          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Utarbeidet av
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Group grow>
              <Text fw={labelWeight}>Navn:</Text>
              <TextInput
                size="sm"
                value={editData.utarbeidetAv?.navn ?? ""}
                onChange={(e) => updateData("utarbeidetAv.navn", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Kontaktperson:</Text>
              <TextInput
                size="sm"
                value={editData.utarbeidetAv?.kontaktPerson ?? ""}
                onChange={(e) => updateData("utarbeidetAv.kontaktPerson", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Adresse:</Text>
              <TextInput
                size="sm"
                value={editData.utarbeidetAv?.adresse ?? ""}
                onChange={(e) => updateData("utarbeidetAv.adresse", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Postnummer:</Text>
              <TextInput
                size="sm"
                value={editData.utarbeidetAv?.postNr ?? ""}
                onChange={(e) => updateData("utarbeidetAv.postNr", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Sted:</Text>
              <TextInput
                size="sm"
                value={editData.utarbeidetAv?.sted ?? ""}
                onChange={(e) => updateData("utarbeidetAv.sted", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Telefon:</Text>
              <TextInput
                size="sm"
                value={editData.utarbeidetAv?.telefon ?? ""}
                onChange={(e) => updateData("utarbeidetAv.telefon", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>E-post:</Text>
              <TextInput
                size="sm"
                value={editData.utarbeidetAv?.email ?? ""}
                onChange={(e) => updateData("utarbeidetAv.email", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>Organisasjonsnummer:</Text>
              <TextInput
                size="sm"
                value={editData.utarbeidetAv?.organisasjonsNr ?? ""}
                onChange={(e) => updateData("utarbeidetAv.organisasjonsNr", e.target.value)}
              />
            </Group>
            <Group grow>
              <Text fw={labelWeight}>ENA autorisasjonsnummer:</Text>
              <TextInput
                size="sm"
                value={editData.utarbeidetAv?.enaAutorisasjonsNr ?? ""}
                onChange={(e) => updateData("utarbeidetAv.enaAutorisasjonsNr", e.target.value)}
              />
            </Group>
          </Stack>

          <Divider
            mt="md"
            label={
              <Text fz="lg" fw={dividerWeight}>
                Anleggsbeskrivelse
              </Text>
            }
            labelPosition="center"
            mb="xs"
          />
          <Stack gap="xs">
            <Text fw={labelWeight}>Beskrivelse av fravik fra anvendte normer:</Text>
            <Textarea
              size="sm"
              minRows={2}
              autosize
              value={editData.fravikFraAnvendteNormer ?? ""}
              onChange={(e) => updateData("fravikFraAnvendteNormer", e.target.value)}
              placeholder="Skriv en beskrivelse av fravik her..."
            />
            <Text mt="sm" fw={labelWeight}>
              Type anlegg:
            </Text>
            <Checkbox.Group
              value={installationTypes}
              onChange={(values) => updateData("anleggsbeskrivelse.typeAnlegg", values.join(", "))}
            >
              <Group gap="md">
                {EKOMNETT_INSTALLATION_TYPES.map((type) => (
                  <Checkbox key={type} value={type} label={type} />
                ))}
              </Group>
            </Checkbox.Group>
            {legacyInstallationTypes.length > 0 && (
              <Text size="sm" c="dimmed">
                Tidligere valg: {legacyInstallationTypes.join(", ")}
              </Text>
            )}
            <Text mt="sm" fw={labelWeight}>
              Utført i henhold til:
            </Text>
            <Group align="center" gap="sm" wrap="nowrap">
              <Checkbox
                label="NEK 700"
                style={{ flex: "0 0 110px" }}
                mt={24}
                checked={nek700 != null}
                onChange={(event) =>
                  updateData("anleggsbeskrivelse.utfoertIht", event.currentTarget.checked
                    ? [{ lovverk: "NEK 700", utgave: "" }, ...otherStandards]
                    : otherStandards)
                }
              />
              <Select
                size="sm"
                label="Angi utgave"
                data={["2012", "2016", "2020", "2024", "2028"]}
                style={{ flex: 1, minWidth: 0 }}
                placeholder="Årstall"
                disabled={nek700 == null}
                value={nek700?.utgave || null}
                onChange={(value) =>
                  updateData("anleggsbeskrivelse.utfoertIht", [{ lovverk: "NEK 700", utgave: value ?? "" }, ...otherStandards])
                }
              />
            </Group>
            <Group align="center" gap="sm" wrap="nowrap">
              <Checkbox
                label="Annet"
                style={{ flex: "0 0 110px" }}
                mt={24}
                checked={otherStandards.length > 0}
                onChange={(event) =>
                  updateData("anleggsbeskrivelse.utfoertIht", [
                    ...(nek700 ? [nek700] : []),
                    ...(event.currentTarget.checked ? [{ lovverk: "", utgave: "" }] : []),
                  ])
                }
              />
              <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                {(otherStandards.length > 0 ? otherStandards : [{ lovverk: "", utgave: "" }]).map((standard, index) => (
                  <Textarea
                    key={index}
                    size="sm"
                    minRows={2}
                    autosize
                    label="Beskriv norm"
                    disabled={otherStandards.length === 0}
                    value={[standard.lovverk, standard.utgave].filter(Boolean).join(" ")}
                    onChange={(event) =>
                      updateData("anleggsbeskrivelse.utfoertIht", [
                        ...(nek700 ? [nek700] : []),
                        ...otherStandards.map((item, itemIndex) =>
                          itemIndex === index ? { lovverk: event.currentTarget.value, utgave: "" } : item,
                        ),
                      ])
                    }
                  />
                ))}
              </Stack>
            </Group>
            <Text mt="sm" fw={labelWeight}>
              Arbeidsbeskrivelse:
            </Text>
            <Textarea
              size="sm"
              minRows={2}
              autosize
              value={editData.anleggsbeskrivelse?.arbeidsbeskrivelse ?? ""}
              onChange={(e) => updateData("anleggsbeskrivelse.arbeidsbeskrivelse", e.target.value)}
              placeholder="Skriv en beskrivelse av arbeidet her..."
            />
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default RapportSamsvarserklaringEkomnett;
